import type { QuestionBriefWasteStreamAllocation, Warning } from '../types/tri.ts';
import type { WasteTransfer } from '../types/questionnaires.ts';
import type { WasteStreamID } from '../types/release-profiles.ts';

export function wasteStreamAllocationWarning(
  question: QuestionBriefWasteStreamAllocation,
  totalToAllocate: number,
  wasteTransfers: (WasteTransfer | undefined)[],
): { status: 'no-warnings' } | { status: 'no-lines' } | { status: 'warning'; warning: Warning } {
  let allocated: number = 0;
  let remainingCount = 0;
  let sawTransfer = false;
  for (const transfer of wasteTransfers) {
    if (transfer?.wasteStreamID === question.wasteStreamID) {
      sawTransfer = true;

      if (typeof transfer.allocationOrAnswer === 'number') {
        allocated += transfer.allocationOrAnswer;
      } else if (transfer.allocationOrAnswer === 'all') {
        allocated += totalToAllocate;
      } else if (transfer.allocationOrAnswer === 'remaining') {
        remainingCount += 1;
      }
    }
  }

  // Don't show a warning if they haven't added a line yet:
  if (!sawTransfer) {
    return { status: 'no-lines' };
  }

  if (remainingCount >= 2) {
    return {
      status: 'warning',
      warning: {
        warningID: 'TW1059',
        type: 'questionnaire',
        subType: wasteTransfers.find((t) => t?.allocationOrAnswer === 'remaining')!.transferID,
        casOrCategory: null,
        message: 'Only one receptor may be marked as taking the "remaining" amount.',
        blocksPriorYearSection8Calculation: true,
      },
    };
  } else if (remainingCount === 1) {
    if (allocated >= totalToAllocate) {
      return {
        status: 'warning',
        warning: {
          warningID: 'TW1060',
          type: 'questionnaire',
          subType: wasteTransfers.find((t) => t?.allocationOrAnswer === 'remaining')!.transferID,
          casOrCategory: null,
          message: 'A receptor was marked as taking the "remaining" amount, but nothing remains to allocate.',
          blocksPriorYearSection8Calculation: true,
        },
      };
    } else {
      allocated = totalToAllocate;
    }
  }

  if (allocated < totalToAllocate) {
    return {
      status: 'warning',
      warning: {
        warningID: 'TW1061',
        type: 'questionnaire',
        subType: question.questionID,
        casOrCategory: null,
        message: `There are ${totalToAllocate} ${question.units} to allocate, but only ${allocated} ${question.units} (too few) has been allocated.`,
        blocksPriorYearSection8Calculation: true,
      },
    };
  } else if (allocated > totalToAllocate) {
    return {
      status: 'warning',
      warning: {
        warningID: 'TW1062',
        type: 'questionnaire',
        subType: question.questionID,
        casOrCategory: null,
        message: `There are ${totalToAllocate} ${question.units} to allocate, but ${allocated} ${question.units} (too many) has been allocated.`,
        blocksPriorYearSection8Calculation: true,
      },
    };
  }

  return { status: 'no-warnings' };
}

export function wasteTransferExistsAndHasAllValues(
  wasteStreamID: WasteStreamID,
  wasteTransfersRaw: (WasteTransfer | undefined)[],
  type: 'wasteStreamDirectInput' | 'wasteStreamAllocation' | 'wasteStreamAttestedZero',
): boolean {
  const transfers = wasteTransfersRaw.filter((t) => !!t && t.wasteStreamID === wasteStreamID) as WasteTransfer[];

  const transferFound = transfers.length > 0;
  let transfersMissingInput = false;
  for (const transfer of transfers) {
    // If present, it is complete:
    if (type === 'wasteStreamDirectInput' && transfer.type === 'nothing') {
      continue;
    }

    if (type === 'wasteStreamDirectInput' && typeof transfer.allocationOrAnswer !== 'number') {
      transfersMissingInput = true;
      break;
    } else if (type === 'wasteStreamAllocation' && transfer.allocationOrAnswer === '') {
      transfersMissingInput = true;
      break;
    }

    // If they explicitly allocate zero, don't worry about any of the type answers, since nothing was transferred:
    if (
      transfer.allocationOrAnswer === 0 &&
      transfer.type === 'offSiteLocation' &&
      type !== 'wasteStreamAttestedZero'
    ) {
      continue;
    }

    if (transfer.type === 'stream') {
      if (!transfer.streamID) {
        transfersMissingInput = true;
        break;
      }
    } else if (transfer.type === 'potw') {
      if (!transfer.potwID) {
        transfersMissingInput = true;
        break;
      }
    } else if (transfer.type === 'offSiteLocation') {
      if (!transfer.offSiteLocationID || transfer.wasteType === '') {
        transfersMissingInput = true;
        break;
      }
    }
  }

  return transferFound && !transfersMissingInput;
}
